/**
 * Metrics Service
 * Main entry point for recording events
 * Uses ad-blocker-resistant naming (metrics instead of analytics)
 */

import { initializeMetrics } from './config';
import { MetricsEventProperties } from '../../types/metrics';
import { createDebugLogger } from '../../utils/debugLogger';
import { processPurchaseEvent, RawPurchaseData } from './utils/purchaseEventPreprocessor';

const logger = createDebugLogger('metrics');

// Initialize metrics instance
const metricsInstance = initializeMetrics();

/**
 * Record an event with standardized payload
 * All calls are wrapped in try-catch to prevent regression
 * 
 * @param eventName - Name of the event
 * @param properties - Event properties/metadata
 */
export const recordEvent = (
    eventName: string | null | undefined,
    properties: MetricsEventProperties = {}
): void => {
    try {
        const safeEventName = (eventName || 'unknown_event').toString();
        const safeProperties = properties && typeof properties === 'object' ? properties : {};

        logger.track(safeEventName, safeProperties);

        if (!metricsInstance) {
            return;
        }

        metricsInstance.recordEvent(safeEventName, safeProperties);
    } catch (error) {
        // Metrics failures should not affect main app
        logger.error('Metrics recording failed:', error);
    }
};

/**
 * Identify a user (for user-specific tracking)
 * 
 * @param userId - User identifier
 * @param traits - User traits/attributes
 */
export const identifyUser = (userId: string | null | undefined, traits: Record<string, any> = {}): void => {
    try {
        const safeUserId = (userId || '').toString();
        const safeTraits = traits && typeof traits === 'object' ? traits : {};

        if (!safeUserId || !metricsInstance) {
            return;
        }

        metricsInstance.identify(safeUserId, safeTraits);
    } catch (error) {
        // Metrics failures should not affect main app
    }
};

/**
 * Record a page view
 * 
 * @param name - Page name
 * @param properties - Page properties
 */
export const recordPageView = (name: string | null | undefined, properties: Record<string, any> = {}): void => {
    try {
        const safeName = (name || 'unknown').toString();
        const safeProperties = properties && typeof properties === 'object' ? properties : {};

        if (!metricsInstance) {
            return;
        }

        metricsInstance.page({
            name: safeName,
            ...safeProperties,
        });
    } catch (error) {
        // Metrics failures should not affect main app
    }
};

/**
 * Record conversion event with purchase data
 * Processes raw purchase data and records conversion event
 * All processing happens in the service layer, not UI
 * 
 * @param eventName - Name of the conversion event
 * @param purchaseData - Raw purchase data to process
 * @param additionalProperties - Additional properties to include
 */
export const recordConversionEvent = (
    eventName: string,
    purchaseData: RawPurchaseData | null | undefined,
    additionalProperties: MetricsEventProperties = {}
): void => {
    try {
        if (!purchaseData || !purchaseData.product || !purchaseData.priceId) {
            // Record event with minimal safe data if purchase data invalid
            recordEvent(eventName, {
                value: 0,
                currency: 'USD',
                ...additionalProperties,
            });
            return;
        }

        // Process purchase data in service layer
        const processedData = processPurchaseEvent(purchaseData);
        
        // Record event with processed data
        recordEvent(eventName, {
            ...processedData,
            ...additionalProperties,
        });
    } catch (error) {
        // Metrics failures should not affect main app
    }
};

// Export default for convenience
const metricsService = {
    recordEvent,
    identifyUser,
    recordPageView,
    recordConversionEvent,
};

export default metricsService;

